import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Bell } from 'lucide-react';
import { usePortalAppearance } from './portalAppearanceContext';
import { getPortalPbToken, getPortalPocketBaseUrl } from '../../constants';
import {
  listKanzleiSeatsForAktePicker,
  listMyPortalAkteMembers,
  listPortalAktenForCustomer,
} from './portalAkten';
import { loadCalendarEventsFromPb } from './calendar/portalCalendarPersist';
import { listPortalDocumentShares } from './portalDocumentShares';
import { fetchPortalProtokollById } from './portalDocuments';
import { readElementRoomsResponse } from './elementSeatInvite';
import {
  filterSitzGlockeEventsForSeat,
  loadSitzGlockeState,
  markSitzGlockeSeen,
  mergeSitzGlockeFromLive,
  saveSitzGlockeState,
  sitzGlockeActorFromSeat,
  sitzGlockeActorSentence,
  sitzGlockeKindLabel,
  sitzGlockeSourceLabel,
  SITZ_GLOCKE_OPEN_EVENT,
  unseenSitzGlockeCount,
  type SitzGlockeEvent,
  type SitzGlockeLiveDoc,
  type SitzGlockeLiveElement,
  type SitzGlockeState,
} from './sitzGlockeStore';

export type PortalSitzGlockeProps = {
  customerId: string;
  kanzleiId?: string;
  /** Desktop: in der Sidebar. Mobile: in der Leiste. */
  placement?: 'sidebar' | 'mobile';
};

const PORTAL_DOCUMENTS_COLLECTION = 'portal_documents';

export const PortalSitzGlocke: React.FC<PortalSitzGlockeProps> = ({
  customerId,
  kanzleiId = '',
  placement = 'sidebar',
}) => {
  const light = usePortalAppearance() === 'light';
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<SitzGlockeState>(() => loadSitzGlockeState(customerId));
  const rootRef = useRef<HTMLDivElement | null>(null);

  const refresh = useCallback(async () => {
    const seatId = customerId.trim();
    const kid = kanzleiId.trim();
    if (!seatId || !kid) return;
    const token = getPortalPbToken()?.trim() || '';
    if (!token) return;
    const prev = loadSitzGlockeState(seatId);
    const pbBase = getPortalPocketBaseUrl().replace(/\/$/, '');
    try {
      const [members, akten, termine, seats, shares, roomsRes] = await Promise.all([
        listMyPortalAkteMembers(token, seatId, kid),
        listPortalAktenForCustomer(seatId, token, kid),
        loadCalendarEventsFromPb({ customerId: seatId, kanzleiId: kid, authToken: token }).catch(() => []),
        listKanzleiSeatsForAktePicker(token, kid).catch(() => []),
        listPortalDocumentShares(token).catch(() => []),
        pbBase
          ? fetch(`${pbBase}/api/arkorbit/element-rooms`, {
              headers: { Authorization: `Bearer ${token}` },
            })
              .then(async (res) => (res.ok ? ((await res.json().catch(() => ({}))) as unknown) : null))
              .catch(() => null)
          : Promise.resolve(null),
      ]);
      if (members === null) return;
      const seatById = new Map(seats.map((row) => [row.id, row]));
      const titleByAkte = new Map(akten.map((row) => [row.id, row.title || 'Akte']));
      const myShares = shares.filter((row) => row.customer_id === seatId && row.kanzlei_id === kid);
      const docMetas = await Promise.all(
        myShares.slice(0, 40).map(async (share) => {
          const doc = await fetchPortalProtokollById(
            pbBase,
            PORTAL_DOCUMENTS_COLLECTION,
            share.document_id,
            token,
          ).catch(() => null);
          return { share, doc };
        }),
      );
      const docs: SitzGlockeLiveDoc[] = [];
      for (const row of docMetas) {
        const ownerId = (row.doc?.customer_id || '').trim();
        const ownerSeat = ownerId ? seatById.get(ownerId) : undefined;
        const actor = sitzGlockeActorFromSeat(ownerSeat || null);
        docs.push({
          documentId: row.share.document_id,
          shareId: row.share.id,
          title: row.doc?.filename || 'Dokument',
          seatId,
          ownerId: ownerId || undefined,
          actorName: actor?.name,
          actorRole: actor?.role,
        });
      }

      const rooms = roomsRes ? readElementRoomsResponse(roomsRes) : [];
      const elements: SitzGlockeLiveElement[] = [];
      for (const room of rooms) {
        const roomSeats = Array.isArray(room.seat_ids) ? room.seat_ids.map((x) => String(x || '').trim()) : [];
        if (!roomSeats.includes(seatId)) continue;
        elements.push({
          roomId: room.id,
          title: room.name || 'Element',
          seatId,
        });
      }

      const next = mergeSitzGlockeFromLive({
        seatId,
        prev,
        members: members.map((row) => ({
          recordId: row.id,
          akteId: row.akte_id,
          title: titleByAkte.get(row.akte_id) || 'Akte',
          seatId: row.customer_id,
        })),
        termine: termine.map((row) => {
          const ownerSeat = seatById.get(row.customerId);
          const actor = sitzGlockeActorFromSeat(ownerSeat || null);
          return {
            id: row.id,
            title: row.title,
            ownerId: row.customerId,
            attendeeIds: row.attendeeIds || [],
            actorName: actor?.name,
            actorRole: actor?.role,
          };
        }),
        docs,
        elements,
      });
      saveSitzGlockeState(next);
      setState(next);
    } catch {
      /* Glocke bleibt mit letztem Stand. */
    }
  }, [customerId, kanzleiId]);

  useEffect(() => {
    setState(loadSitzGlockeState(customerId));
    void refresh();
    const onFocus = () => {
      void refresh();
    };
    window.addEventListener('focus', onFocus);
    const timer = window.setInterval(() => {
      void refresh();
    }, 60_000);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.clearInterval(timer);
    };
  }, [customerId, refresh]);

  useEffect(() => {
    const onOpen = () => {
      setOpen((wasOpen) => {
        if (wasOpen) return true;
        const marked = markSitzGlockeSeen(loadSitzGlockeState(customerId));
        saveSitzGlockeState(marked);
        setState(marked);
        return true;
      });
    };
    window.addEventListener(SITZ_GLOCKE_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(SITZ_GLOCKE_OPEN_EVENT, onOpen);
  }, [customerId]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (event: MouseEvent) => {
      const root = rootRef.current;
      if (!root) return;
      if (event.target instanceof Node && root.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const unseen = unseenSitzGlockeCount(state);
  const rows: SitzGlockeEvent[] = filterSitzGlockeEventsForSeat(state.events, customerId);

  const toggle = () => {
    setOpen((wasOpen) => {
      const nextOpen = !wasOpen;
      if (nextOpen) {
        const marked = markSitzGlockeSeen(loadSitzGlockeState(customerId));
        saveSitzGlockeState(marked);
        setState(marked);
      }
      return nextOpen;
    });
  };

  return (
    <div
      ref={rootRef}
      className={placement === 'mobile' ? 'relative shrink-0' : 'relative ml-auto shrink-0'}
      data-testid="portal-sitz-glocke"
    >
      <button
        type="button"
        data-testid="portal-sitz-glocke-btn"
        aria-label={unseen > 0 ? `Benachrichtigungen, ${unseen} ungesehen` : 'Benachrichtigungen'}
        aria-expanded={open}
        onClick={toggle}
        className={`relative rounded-lg p-2 ${light ? 'text-[#1c1915] hover:bg-[#f3efe8]' : 'text-slate-300 hover:bg-white/[0.06]'} ${
          unseen > 0 ? (light ? 'text-[#5b4678]' : 'text-violet-200') : ''
        }`}
      >
        <Bell className="h-4 w-4" aria-hidden />
        {unseen > 0 ? (
          <span
            data-testid="portal-sitz-glocke-badge"
            className="absolute -right-0.5 -top-0.5 min-w-[1rem] rounded-full bg-violet-500/90 px-1 text-[9px] font-semibold leading-4 text-white"
          >
            {unseen > 9 ? '9+' : unseen}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          data-testid="portal-sitz-glocke-panel"
          role="dialog"
          aria-label="Benachrichtigungen dieses Sitzes"
          className={`absolute z-[60] w-[min(18rem,calc(100vw-1.5rem))] overflow-hidden rounded-lg border border-white/[0.08] bg-slate-950/95 shadow-xl backdrop-blur ${
            placement === 'mobile' ? 'right-0 top-full mt-1' : 'left-0 top-full mt-1'
          }`}
        >
          <div className="border-b border-white/[0.06] px-3 py-2">
            <p className="m-0 text-[11px] font-semibold text-slate-200">Für diesen Sitz</p>
            <p className="m-0 mt-0.5 text-[10px] text-slate-500">Nur echte Vorgänge · keine Beispiele</p>
          </div>
          {rows.length === 0 ? (
            <p
              data-testid="portal-sitz-glocke-empty"
              className="m-0 px-3 py-4 text-center text-[11px] text-slate-500"
            >
              Nichts Neues
            </p>
          ) : (
            <ul className="m-0 max-h-64 list-none space-y-0 overflow-y-auto p-0">
              {rows.map((row) => {
                const actorLine = sitzGlockeActorSentence(row.kind, row.actorName, row.actorRole);
                return (
                  <li
                    key={row.id}
                    data-testid={`portal-sitz-glocke-row-${row.kind}`}
                    data-glocke-kind={row.kind}
                    data-glocke-source={row.source}
                    data-glocke-actor={row.actorName || ''}
                    className="border-t border-white/[0.04] px-3 py-2 first:border-t-0"
                  >
                    <p className="m-0 text-[10px] font-semibold uppercase tracking-wide text-violet-300/90">
                      {sitzGlockeKindLabel(row.kind)}
                    </p>
                    {actorLine ? (
                      <p className="m-0 mt-0.5 text-[11px] text-slate-200" data-testid="portal-sitz-glocke-actor">
                        {actorLine}
                      </p>
                    ) : null}
                    <p className="m-0 mt-0.5 text-[11px] text-slate-400">
                      {sitzGlockeSourceLabel(row.source)}
                      {row.title ? ` · ${row.title}` : ''}
                    </p>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
};
